import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import {
  Snackbar,
  Alert,
  AlertColor,
  Slide,
  SlideProps,
  Stack,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

interface Toast {
  id: string
  message: string | ReactNode
  type: AlertColor
  duration?: number
  icon?: ReactNode
}

interface SnackbarContextType {
  showToast: (
    message: string | ReactNode,
    type?: AlertColor,
    options?: { duration?: number; icon?: ReactNode },
  ) => void
  showSuccess: (
    message: string | ReactNode,
    options?: { duration?: number },
  ) => void
  showError: (
    message: string | ReactNode,
    options?: { duration?: number },
  ) => void
  showWarning: (
    message: string | ReactNode,
    options?: { duration?: number },
  ) => void
  showInfo: (
    message: string | ReactNode,
    options?: { duration?: number },
  ) => void
  closeToast: (id: string) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined,
)

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />
}

// Site color standards from variables.scss
const alertStyles = {
  success: {
    backgroundColor: '#D4EDDA',
    color: '#07135f',
    '& .MuiAlert-icon': {
      color: '#00864d',
    },
  },
  error: {
    backgroundColor: '#FFE6F1',
    color: '#07135f',
    '& .MuiAlert-icon': {
      color: '#d00026',
    },
  },
  warning: {
    backgroundColor: '#FFEEBA',
    color: '#07135f',
    '& .MuiAlert-icon': {
      color: '#f9b234',
    },
  },
  info: {
    backgroundColor: '#FFEEBA',
    color: '#07135f',
    '& .MuiAlert-icon': {
      color: '#0923e6',
    },
  },
}

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const generateId = () =>
    `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  const showToast = useCallback(
    (
      message: string | ReactNode,
      type: AlertColor = 'info',
      options?: { duration?: number; icon?: ReactNode },
    ) => {
      const id = generateId()
      const newToast: Toast = {
        id,
        message,
        type,
        duration: options?.duration ?? 4000,
        icon: options?.icon,
      }
      setToasts(prev => [...prev, newToast])
    },
    [],
  )

  const showSuccess = useCallback(
    (message: string | ReactNode, options?: { duration?: number }) => {
      showToast(message, 'success', options)
    },
    [showToast],
  )

  const showError = useCallback(
    (message: string | ReactNode, options?: { duration?: number }) => {
      showToast(message, 'error', options)
    },
    [showToast],
  )

  const showWarning = useCallback(
    (message: string | ReactNode, options?: { duration?: number }) => {
      showToast(message, 'warning', options)
    },
    [showToast],
  )

  const showInfo = useCallback(
    (message: string | ReactNode, options?: { duration?: number }) => {
      showToast(message, 'info', options)
    },
    [showToast],
  )

  const closeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const handleClose =
    (id: string) =>
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return
      }
      closeToast(id)
    }

  const contextValue = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeToast,
  }

  // Set the singleton instance for non-hook usage
  React.useEffect(() => {
    setSnackbarInstance(contextValue)
    return () => {
      setSnackbarInstance(null as any)
    }
  }, [contextValue])

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Stack
        spacing={1}
        sx={{
          position: 'fixed',
          top: 77,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: 0,
          },
        }}
      >
        {toasts.map(toast => (
          <Snackbar
            key={toast.id}
            open={true}
            autoHideDuration={toast.duration}
            onClose={handleClose(toast.id)}
            slots={{ transition: SlideTransition }}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
              position: 'relative',
              top: 'auto !important',
              right: 'auto !important',
              left: 'auto !important',
              bottom: 'auto !important',
              transform: 'none !important',
            }}
          >
            <Alert
              severity={toast.type}
              variant="filled"
              icon={toast.icon !== undefined ? toast.icon : undefined}
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => closeToast(toast.id)}
                  sx={{ color: '#07135f' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                width: '100%',
                minWidth: 250,
                maxWidth: 450,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                fontWeight: 600,
                fontSize: '14px',
                padding: '4px 12px',
                fontFamily: "'Mulish', sans-serif",
                borderRadius: '8px',
                '& .MuiAlert-message': {
                  display: 'flex',
                  alignItems: 'center',
                },
                ...alertStyles[toast.type],
              }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}

// Singleton instance for non-hook usage (like in helper.service.tsx)
let snackbarInstance: SnackbarContextType | null = null

export const setSnackbarInstance = (instance: SnackbarContextType) => {
  snackbarInstance = instance
}

export const getSnackbarInstance = (): SnackbarContextType | null => {
  return snackbarInstance
}
